/**
 * Verified public destinations for the subscription journeys (terms, privacy,
 * store listings, review link, support mailbox), lifted from the design
 * reference `completion.js` `VERIFIED_DESTINATIONS` (checked 2026-09-21).
 *
 * Platform-free: callers pass the platform (`Platform.OS`, or the web
 * user-agent classification from `reviewPlatformFromUserAgent`).
 */

export type JourneyLocale = 'en' | 'ar';
export type ReviewPlatform = 'ios' | 'android' | 'web';

export const VERIFIED_DESTINATIONS = Object.freeze({
  checkedAt: '2026-09-21',
  terms: Object.freeze({
    en: 'https://www.tabbio.com/en/terms-of-service',
    ar: 'https://www.tabbio.com/ar/terms-of-service',
  }),
  privacy: Object.freeze({
    en: 'https://www.tabbio.com/en/privacy-policy',
    ar: 'https://www.tabbio.com/ar/privacy-policy',
  }),
  appStore:
    'https://apps.apple.com/us/app/tabbio-cv-maker-job-search/id6755083658',
  appStoreReview:
    'https://apps.apple.com/us/app/tabbio-cv-maker-job-search/id6755083658?action=write-review',
  googlePlay: 'https://play.google.com/store/apps/details?id=com.tabbio.app',
  supportEmail: 'ahmed@tabbio.com',
  generalContactEmail: 'hello@tabbio.com',
});

export function toJourneyLocale(language: string | null | undefined): JourneyLocale {
  return language?.toLowerCase().startsWith('ar') ? 'ar' : 'en';
}

export function termsUrl(locale: string | null | undefined) {
  return VERIFIED_DESTINATIONS.terms[toJourneyLocale(locale)];
}

export function privacyUrl(locale: string | null | undefined) {
  return VERIFIED_DESTINATIONS.privacy[toJourneyLocale(locale)];
}

/** The platform's own store listing (install invite, L3). */
export function storeListingUrl(store: 'apple' | 'google') {
  return store === 'apple'
    ? VERIFIED_DESTINATIONS.appStore
    : VERIFIED_DESTINATIONS.googlePlay;
}

/**
 * Where "Looks good" / "Review Tabbio" goes. Native and mobile web open the
 * device's store review destination directly; desktop web shows a store
 * chooser ("Choose your store") because the device's store is unknown.
 * This is an ordinary external link - never a native review API call.
 */
export function reviewDestinationForPlatform(
  platform: ReviewPlatform,
): { kind: 'url'; url: string } | { kind: 'chooser' } {
  if (platform === 'ios') {
    return { kind: 'url', url: VERIFIED_DESTINATIONS.appStoreReview };
  }
  if (platform === 'android') {
    return { kind: 'url', url: VERIFIED_DESTINATIONS.googlePlay };
  }
  return { kind: 'chooser' };
}

/** Classifies a browser for review routing (iPadOS may report a Mac user agent). */
export function reviewPlatformFromUserAgent(
  userAgent: string,
  maxTouchPoints = 0,
): ReviewPlatform {
  if (/android/i.test(userAgent)) return 'android';
  if (
    /iphone|ipad|ipod/i.test(userAgent) ||
    (/macintosh/i.test(userAgent) && maxTouchPoints > 1)
  ) {
    return 'ios';
  }
  return 'web';
}

/**
 * Public review links for Account/Billing ("Rate Tabbio"). Always available,
 * independent of any feedback answer. Filter by OS only.
 */
export function publicReviewLinks(
  platform: ReviewPlatform | 'all' = 'all',
): { store: 'apple' | 'google'; url: string }[] {
  const links: { store: 'apple' | 'google'; url: string }[] = [];
  if (platform !== 'android') {
    links.push({ store: 'apple', url: VERIFIED_DESTINATIONS.appStoreReview });
  }
  if (platform !== 'ios') {
    links.push({ store: 'google', url: VERIFIED_DESTINATIONS.googlePlay });
  }
  return links;
}

/** NUL-stripped, capped at 1,000 user characters, trimmed. */
export function cleanSupportDetails(details: unknown) {
  return String(details ?? '')
    .replace(/\u0000/g, '')
    .slice(0, 1000)
    .trim();
}

/**
 * An explicit mailto draft (L1b error fallback), not a send operation. Only the
 * user's own note is added; no identity, CV or diagnostics. Copy is verbatim
 * from the design reference and belongs to the email, not the UI locale files.
 */
export function createSupportMailto(details = '', locale: string = 'en') {
  const ar = toJourneyLocale(locale) === 'ar';
  const subject = ar
    ? 'مساعدة في سيرتي الذاتية على تابيو'
    : 'Help with my Tabbio CV';
  const introduction = ar
    ? 'مرحباً فريق تابيو، أحتاج مساعدة في سيرتي الذاتية.'
    : 'Hi Tabbio team, I would like help with my CV.';
  const message = cleanSupportDetails(details);
  const body = `${introduction}${message ? `\n\n${message}` : ''}`;
  return `mailto:${VERIFIED_DESTINATIONS.supportEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
